import { ChevronDownIcon } from '@heroicons/react/24/outline'
import {
  MenuItem,
  Typography,
  MenuHandler,
  ListItem,
  MenuList,
  Collapse,
  Menu,
} from '@material-tailwind/react'
import React, { useState } from 'react'
import { Link } from 'gatsby'
import { navListMenuItems } from './utils'
import { BlogColumn } from './blogColumn'

const ItemLink = ({
  title,
  description,
  icon,
  link,
  isExternal = false,
  isDisabled = false,
}: {
  title: string
  description: string
  icon: React.ForwardRefExoticComponent<
    Omit<React.SVGProps<SVGSVGElement>, 'ref'> & {
      title?: string | undefined
      titleId?: string | undefined
    } & React.RefAttributes<SVGSVGElement>
  >
  link: string
  isExternal?: boolean
  isDisabled?: boolean
}) =>
  isExternal ? (
    <a
      href={link}
      key={title}
      target="_blank"
      rel="noreferrer"
      title={title}
      aria-disabled={isDisabled}
      className="disabled:cursor-not-allowed"
    >
      <MenuItem className="flex items-start md:gap-2 lg:gap-3 text-night">
        <div className="flex items-center justify-center mr-2 md:mr-0">
          {' '}
          {React.createElement(icon, {
            strokeWidth: 2,
            className: 'h-6 text-night w-6',
          })}
        </div>
        <div>
          <p className="flex items-center text-sm font-medium text-night">
            {title}
          </p>
          <Typography
            variant="paragraph"
            className="text-xs font-thin text-night"
          >
            {description}
          </Typography>
        </div>
      </MenuItem>
    </a>
  ) : (
    <Link
      key={link}
      to={link}
      title={title}
      aria-disabled={isDisabled}
      className="disabled:cursor-not-allowed"
    >
      <MenuItem className="flex items-start gap-3 text-night">
        <div className="flex items-center justify-center">
          {' '}
          {React.createElement(icon, {
            strokeWidth: 2,
            className: 'h-6 text-night w-6',
          })}
        </div>
        <div>
          <p className="flex items-center text-sm font-medium text-night">
            {title}
          </p>
          <Typography
            variant="paragraph"
            className="text-xs font-thin text-night"
          >
            {description}
          </Typography>
        </div>
      </MenuItem>
    </Link>
  )

export const CollapsedMenu = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  function getCatgorieTranslation(category: string) {
    switch (category) {
      case 'firm':
        return "Pour les cabinets d'expertise comptable"
      case 'candidate':
        return 'Pour les candidats'

      case 'ressources':
        return 'Nos ressources'
      default:
        return ''
    }
  }

  const renderItems = Object.keys(navListMenuItems).map((category) => (
    <li className="space-y-3" key={category}>
      <p className="pl-3 font-semibold text-night">
        {getCatgorieTranslation(category)}
      </p>
      <ul className="space-y-2">
        {navListMenuItems[category].map(
          ({ icon, title, description, link, isDisabled, isExternal }) => (
            <li role="menuitem" key={title}>
              <ItemLink
                icon={icon}
                isDisabled={isDisabled}
                isExternal={isExternal}
                link={link}
                title={title}
                description={description}
              />
            </li>
          ),
        )}
      </ul>
    </li>
  ))

  const linkClassName =
    'font-medium text-[13px] leading-[23.4px] text-primary hover:text-blue-800 transition-colors duration-300 ease-in-out xl:text-[15px] xl:leading-[27px] xl:tracking-[0.6px]'

  function handleMenuClick() {
    setIsMobileMenuOpen(!isMobileMenuOpen)
    setIsMenuOpen(!isMobileMenuOpen)
  }

  return (
    <>
      <Menu
        open={isMenuOpen}
        handler={setIsMenuOpen}
        offset={{ mainAxis: 1 }}
        placement="bottom"
        allowHover
      >
        <MenuHandler>
          <Typography as="div" variant="small">
            <ListItem
              className={`mt-2 md:mt-0 flex items-center gap-2 p-0 md:p-2 pr-4 ${
                isMenuOpen && 'font-bold w-full'
              }`}
              selected={isMenuOpen || isMobileMenuOpen}
              onClick={() => handleMenuClick()}
            >
              <span className={linkClassName}>Nos solutions</span>
              <ChevronDownIcon
                strokeWidth={2.5}
                className={`hidden h-3 w-3 transition-transform lg:block text-primary
						hover:text-blue-800 duration-300 ease-in-out ${isMenuOpen ? 'rotate-180' : ''}`}
                title={isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              />
              <ChevronDownIcon
                strokeWidth={2.5}
                className={`block h-3 w-3 transition-transform text-primary
						hover:text-blue-800 duration-300 ease-in-out lg:hidden ${
              isMobileMenuOpen ? 'rotate-180' : ''
            }`}
                title={isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              />
            </ListItem>
          </Typography>
        </MenuHandler>
        <MenuList className="hidden w-full grid-cols-4 p-0 border-b rounded-none lg:grid bg-very-light-blue border-b-night/40">
          <div className="col-span-3 pl-4">
            <ul className="grid grid-cols-3 py-6 outline-none outline-0">
              {renderItems}
            </ul>
          </div>
          <div className="col-span-1">
            <BlogColumn />
          </div>
        </MenuList>
      </Menu>
      <div className="block lg:hidden">
        <Collapse open={isMobileMenuOpen}>{renderItems}</Collapse>
      </div>
    </>
  )
}
